import { useQuery as useApolloQuery } from '@apollo/client';
import { useFeatureFlag } from '@newfront-insurance/admin-ui';
import { Portal } from '@newfront-insurance/core-ui';
import { datadogRum } from '@newfront-insurance/dd-rum';
import type { AuthProviderContext } from '@newfront-insurance/next-auth';
// eslint-disable-next-line no-restricted-imports -- Host integration reads the host route; Nova's internal navigation stays in memory.
import { useQuery, useRouter } from '@newfront-insurance/next-router-provider';
import {
  NovaChatHostContextProvider,
  NovaChatOverlay,
  type HostApp,
  type NovaChatContextBackend,
  type NovaHostContextPatch,
} from '@newfront-insurance/nova-loader';
import { useProvider, type Provider as ProvisionProvider } from '@newfront-insurance/react-provision';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { NovaChatContainer, NovaProvider, useNovaWrapperState, type NovaProviderCallbacks } from '../..';
import { GET_ACCOUNT } from '../../../../queries/chats';
import { FloatingButton } from '../../components/floating-button';
import { useChatOverlayContext } from '../../contexts/chat-overlay-context';
import { resolvePageAccountUuid } from '../../lib/account-bootstrap';

interface ChatOverlayProviderV2Props {
  children: React.ReactNode;
  authProvider: ProvisionProvider<AuthProviderContext>;
  hostApp?: HostApp;
  transparentFloatingButton?: boolean;
  callbacks?: NovaProviderCallbacks;
  initialAccountUuid?: string;
}

interface HostContextTarget {
  setAccountUuid(uuid?: string): void;
}

interface HostContextRegistry {
  target: HostContextTarget | null;
  buffered: NovaHostContextPatch;
}

/**
 * Migration wrapper that can render either the in-bundle chat or the remote Nova embed.
 * Host descendants always receive the stable nova-loader context API in both branches.
 */
export function ChatOverlayProviderV2({
  children,
  authProvider,
  hostApp,
  transparentFloatingButton,
  callbacks,
  initialAccountUuid,
}: ChatOverlayProviderV2Props) {
  const { router } = useRouter();
  const isChatOverlayEnabled = useFeatureFlag('nova-chat-overlay', false);
  const embedFlag = useFeatureFlag<HostApp[] | false>('nova-chat-embed-apps', []);
  const embedApps = Array.isArray(embedFlag) ? embedFlag : [];
  const [handshakeFailed, setHandshakeFailed] = useState(false);
  const isDebugRoute = router.pathname.startsWith('/chat/debug');
  const isStandaloneMode = router.pathname.startsWith('/chat') && !isDebugRoute;
  const { userDetails } = useProvider(authProvider);
  const { uuid: userUuid, scopes } = userDetails ?? {};
  const queryAccountUuid = useQuery<string | undefined>('accountUuid');
  const requestedPageAccountUuid = resolvePageAccountUuid({ initialAccountUuid, queryAccountUuid });
  const renderOverlay = isChatOverlayEnabled && Boolean(userUuid) && !isDebugRoute;
  const {
    data: pageAccountData,
    loading: pageAccountLoading,
    error: pageAccountError,
  } = useApolloQuery(GET_ACCOUNT, {
    variables: { uuid: requestedPageAccountUuid ?? '' },
    skip: !renderOverlay || !requestedPageAccountUuid,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  // Summary-only account pages still have a UUID in the URL. Bootstrap Nova only
  // after the protected account query succeeds, and block URL fallback otherwise.
  const pageAccountUuid =
    requestedPageAccountUuid &&
    !pageAccountLoading &&
    !pageAccountError &&
    pageAccountData?.getAccount?.uuid === requestedPageAccountUuid
      ? requestedPageAccountUuid
      : null;
  const registryRef = useRef<HostContextRegistry>({ target: null, buffered: {} });
  const [hostContextPatch, setHostContextPatch] = useState<NovaHostContextPatch>({});
  const embedContextPatch = useMemo<NovaHostContextPatch>(
    () => ({ accountUuid: pageAccountUuid, ...hostContextPatch }),
    [pageAccountUuid, hostContextPatch],
  );
  const registryBackend = useMemo<NovaChatContextBackend>(
    () => ({
      setContextPatch: (patch) => {
        registryRef.current.buffered = { ...registryRef.current.buffered, ...patch };
        setHostContextPatch((previous) => ({ ...previous, ...patch }));
        forwardPatch(registryRef.current.target, patch);
      },
    }),
    [],
  );
  // Callbacks are an in-bundle API until the bridge protocol explicitly supports them.
  const useEmbed = Boolean(hostApp && !callbacks && embedApps.includes(hostApp) && !handshakeFailed);

  const handleTelemetry = useCallback(
    ({ name, data }: { name: string; data?: Record<string, unknown> }) => {
      datadogRum.addAction(`nova_embed.${name}`, { hostApp, ...data });
    },
    [hostApp],
  );
  const handleFatalError = useCallback(
    (reason: 'handshake-timeout' | 'incompatible-embed' | 'nova-error' | 'killswitch') => {
      datadogRum.addAction('nova_embed.fallback_in_bundle', { hostApp, reason });
      setHandshakeFailed(true);
    },
    [hostApp],
  );

  return (
    <NovaChatHostContextProvider backend={registryBackend}>
      {children}
      {useEmbed && renderOverlay && hostApp ? (
        <NovaChatOverlay
          hostApp={hostApp}
          authProvider={authProvider}
          transparentFloatingButton={transparentFloatingButton}
          contextPatch={embedContextPatch}
          onFatalError={handleFatalError}
          onTelemetry={handleTelemetry}
        />
      ) : renderOverlay ? (
        <Portal name="chat-overlay-portal" portalZIndex={1000}>
          <NovaProvider
            userUuid={userUuid as string}
            scopes={scopes}
            authProvider={authProvider}
            isStandaloneMode={isStandaloneMode}
            initialAccountUuid={pageAccountUuid}
            callbacks={callbacks}
          >
            <HostContextRegistrar registryRef={registryRef} />
            <FloatingButtonWrapper transparentFloatingButton={transparentFloatingButton} />
            <NovaChatContainer />
          </NovaProvider>
        </Portal>
      ) : null}
    </NovaChatHostContextProvider>
  );
}

function HostContextRegistrar({ registryRef }: { registryRef: React.RefObject<HostContextRegistry> }) {
  const { setAccountUuid } = useChatOverlayContext();

  useEffect(() => {
    const target = { setAccountUuid };
    const registry = registryRef.current;
    registry.target = target;
    forwardPatch(target, registry.buffered);
    return () => {
      if (registry.target === target) registry.target = null;
    };
  }, [registryRef, setAccountUuid]);

  return null;
}

function forwardPatch(target: HostContextTarget | null, patch: NovaHostContextPatch): void {
  if (!target) return;
  if ('accountUuid' in patch) target.setAccountUuid(patch.accountUuid ?? undefined);
}

function FloatingButtonWrapper({ transparentFloatingButton }: { transparentFloatingButton?: boolean }) {
  const { isChatVisible, isFloatingButtonVisible, hasNotifications, handleToggle } = useNovaWrapperState();
  const { router } = useRouter();
  const isStandaloneMode = router.pathname.startsWith('/chat') && !router.pathname.startsWith('/chat/debug');

  if (!isFloatingButtonVisible || isStandaloneMode || isChatVisible) return null;

  return (
    <FloatingButton
      onClick={handleToggle}
      hasNotifications={hasNotifications}
      isTransparent={transparentFloatingButton}
    />
  );
}
